import { apiBaseUrl } from '../platform/index.js';
import { supabaseBrowser } from './supabaseClient.js';

/**
 * Invites client ops — thin wrappers around the v0.9.8 invite
 * endpoints. No local cache: host sees invites via Realtime UPDATE
 * on game_seats (LobbyScene refetches on row insert), invitee sees
 * invites via the inbox refetch path (InboxScene refetches on
 * mount). Mutation callers don't need to invalidate anything; the
 * data flow self-corrects.
 *
 * Why a module-level "ops" file instead of store-state plumbing:
 * unlike friends.ts / openGames.ts, the data the host + invitee
 * consume already lives in their respective realtime / inbox
 * caches. We just need typed wrappers around the POST + DELETE
 * endpoints so LobbyScene + InboxScene don't reach for raw fetch.
 *
 * Slice 1.7 of v1.0/invites.
 */

// ============================================================
// Result types
// ============================================================

/** sendInvite result. Success carries the new held seat's details
 *  so LobbyScene can confirm ("Invite sent to alice — seat 2") in
 *  the UI without a refetch round-trip. */
export type SendInviteResult =
  | {
      readonly ok: true;
      readonly seatIndex: number;
      readonly invitedUsername: string;
      readonly invitedUserId: string;
    }
  | {
      readonly ok: false;
      readonly error: string;
      /** HTTP status. Lets the UI render specific copy by code:
       *  404 → "username not found", 409 → "already in game", 429
       *  → "too many invites — try again later", etc. */
      readonly status: number;
    };

/** cancelInvite result. `cancelledBy` lets telemetry / UX distinguish
 *  the two paths sharing the endpoint (host cancelling vs invitee
 *  declining). */
export type CancelInviteResult =
  | {
      readonly ok: true;
      readonly seatIndex: number;
      readonly cancelledBy: 'host' | 'invitee';
    }
  | {
      readonly ok: false;
      readonly error: string;
      readonly status: number;
    };

// ============================================================
// Network ops
// ============================================================

async function authHeader(): Promise<HeadersInit> {
  const supabase = supabaseBrowser();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function readError(
  res: Response
): Promise<{ error: string; status: number }> {
  const body = (await res.json().catch(() => null)) as {
    error?: string;
  } | null;
  return {
    error: body?.error ?? `${res.status}`,
    status: res.status,
  };
}

/**
 * POST /api/games/:id/invite — host reserves a held seat for
 * `username`. Only the seated host (seat 0) can call this; the
 * server enforces and returns 403 otherwise.
 */
export async function sendInvite(
  gameId: string,
  username: string
): Promise<SendInviteResult> {
  try {
    const res = await fetch(
      `${apiBaseUrl()}/api/games/${encodeURIComponent(gameId)}/invite`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(await authHeader()),
        },
        body: JSON.stringify({ username }),
      }
    );
    if (!res.ok) return { ok: false, ...(await readError(res)) };
    const body = (await res.json()) as {
      seatIndex: number;
      invitedUsername: string;
      invitedUserId: string;
    };
    return {
      ok: true,
      seatIndex: body.seatIndex,
      invitedUsername: body.invitedUsername,
      invitedUserId: body.invitedUserId,
    };
  } catch (err: unknown) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
      status: 0,
    };
  }
}

/**
 * DELETE /api/games/:id/invite?seatIndex=N — cancel a pending
 * invite. Authorized for EITHER the host (cancelling any seat) OR
 * the invitee of the targeted seat (declining their own); the
 * server gates this and the response's `cancelledBy` field
 * confirms which path applied.
 */
export async function cancelInvite(
  gameId: string,
  seatIndex: number
): Promise<CancelInviteResult> {
  try {
    const url = `${apiBaseUrl()}/api/games/${encodeURIComponent(
      gameId
    )}/invite?seatIndex=${encodeURIComponent(String(seatIndex))}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: await authHeader(),
    });
    if (!res.ok) return { ok: false, ...(await readError(res)) };
    const body = (await res.json()) as {
      seatIndex: number;
      cancelledBy: 'host' | 'invitee';
    };
    return {
      ok: true,
      seatIndex: body.seatIndex,
      cancelledBy: body.cancelledBy,
    };
  } catch (err: unknown) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
      status: 0,
    };
  }
}
